import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import {
  Divider,
  TextField,
  Stack,
  Typography,
  Box,
  Button,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Camera } from 'iconsax-react';
import { setIsLoading, setSnackBar } from '../../../store/globalSlice';
import { useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import ApiClient from '../../../config/api';
import { submitStatusChange } from './submitStatusChange';

const RejectParcel = ({
  location,
  setOpenDetail,
  selectedParcel,
  selectedTask,
  openGetParcel,
  selectedStatus,
  setOpenGetParcel,
}) => {
  const [description, setDescription] = useState('');
  const dispatch = useDispatch();
  const theme = useTheme();
  const api = ApiClient();
  const completedIds = useRef(new Set());
  const submitting = useRef(false);
  useEffect(() => {
    completedIds.current = new Set();
  }, [openGetParcel]);

  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [openCamera, setOpenCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  const getPermissionNative = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ data: 'camera-permission' })
      );
    }
  };

  const requestCameraAccessWeb = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      setCameraStream(mediaStream);
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL('image/png');
        setCapturedImage(imageDataUrl);
      }
    }

    stopCamera();
  };

  useEffect(() => {
    if (openGetParcel) {
      setCapturedImage(null);
      requestCameraAccessWeb();
      getPermissionNative();
      setOpenCamera(true);
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [openGetParcel]);

  const handleSubmit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      dispatch(setIsLoading(true));
      await submitStatusChange({
        api,
        scope: openGetParcel,
        selectedTask,
        selectedParcel,
        completedIds: completedIds.current,
        data: {
          images: capturedImage ? [capturedImage] : [],
          status: selectedStatus?.key,
          description,
          lng: location?.coords.longitude || 0,
          lat: location?.coords.latitude || 0,
        },
      });
      setOpenGetParcel('');
      setOpenDetail(false);
    } catch (error) {
      dispatch(
        setSnackBar({ message: error?.response?.data?.message || error.message, severity: 'error' })
      );
      console.error('Error saving signature:', error);
    } finally {
      submitting.current = false;
      dispatch(setIsLoading(false));
    }
  };
  return (
    <React.Fragment>
      <Dialog
        onClose={() => setOpenGetParcel('')}
        aria-labelledby="customized-dialog-title"
        open={Boolean(openGetParcel)}
        maxWidth="sm"
        fullWidth={true}
        fullScreen={true}
      >
        <Stack
          sx={{
            position: 'sticky',
            pt: 2,
            top: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Typography fontSize={20} fontWeight={600} textAlign={'right'}>
            {openGetParcel?.label || 'دریافت مرسوله'}
          </Typography>
          <KeyboardBackspaceIcon
            className={'clickable'}
            sx={{
              transform: 'rotate(180deg)',
              cursor: 'pointer',
              marginRight: '8px',
              marginLeft: '8px',
              fontSize: '24px',
            }}
            onClick={() => {
              setOpenGetParcel('');
            }}
          />
        </Stack>
        <Divider
          sx={{
            width: '100%',
            marginTop: '10px',
          }}
        />

        <DialogContent sx={{ pt: '0px', mt: 2 }}>
          <Box
            sx={{
              minHeight: '300px',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.7,
              },
              borderRadius: '16px',
              bgcolor: 'background.card',
              border: '1px solid',
              borderColor: 'divider',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              p: 2,
            }}
            onClick={() => {
              if (!openCamera) {
                requestCameraAccessWeb();
                getPermissionNative();
                setOpenCamera(true);
              }
            }}
          >
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {!openCamera ? (
              <>
                <Camera size="48" color={theme.palette.text.primary} />
                <Typography
                  textAlign={'center'}
                  variant="subtitle1"
                  display={'flex'}
                >
                  باز کردن دوربین
                </Typography>
              </>
            ) : (
              <>
                {' '}
                {capturedImage ? (
                  <img
                    src={capturedImage}
                    alt="Captured"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      maxHeight: '300px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #D4D4D8',
                    }}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      borderRadius: '8px',
                      border: '1px solid #D4D4D8',
                      background: 'black',
                    }}
                  />
                )}
              </>
            )}
          </Box>
          {capturedImage ? (
            <>
              <TextField
                label="توضیحات"
                multiline
                rows={2}
                placeholder="توضیحات را در صورت نیاز وارد نمایید"
                sx={{ width: '100%', mt: 3 }}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                inputProps={{ style: { textAlign: 'right' } }}
              />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                sx={{ mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setCapturedImage(null);
                    requestCameraAccessWeb();
                    getPermissionNative();
                    setOpenCamera(true);
                  }}
                  sx={{
                    m: '0px 4px 0px 4px !important',
                  }}
                >
                  گرفتن مجدد
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleSubmit();
                  }}
                  sx={{
                    color: 'primary.contrastText',
                    m: '0px 4px 0px 4px !important',
                  }}
                >
                  تایید
                </Button>
              </Stack>
            </>
          ) : (
            <Button
              variant="contained"
              component="label"
              sx={{
                display: 'block',
                mx: 'auto',
                mt: 2,
                textAlign: 'center',
                color: 'primary.contrastText',
              }}
              onClick={captureImage}
            >
              گرفتن
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default RejectParcel;
